import { FC, ReactNode } from 'react';
import { capitalCase } from 'change-case';
import { Gavel, Group, LucideImport, Plug } from 'lucide-react';
import { TbArrowRight, TbNews, TbTransactionDollar } from 'react-icons/tb';

import { FilterButtonGroup, FilterButtonGroupSegment } from '@/shared';
import { dayjs } from '@/utilities';

interface PremiumOrEventFilterItemProps {
    filters: any;
    handleDelete: (path: string) => void;
    label: string;
    path: string;
    value: any;
}

const iconStyle = { verticalAlign: '-0.075rem', marginRight: '0.25rem' } as const;

const keyNameMap: Record<string, string> = {
    connections: 'Connected Sources',
    imports: 'Imports',
    insiderTransactions: 'Insider Transactions',
    ipos: 'IPOs',
    mAndA: 'M&A',
    news: 'News',
};

const keyIconMap: Record<string, ReactNode> = {
    connections: <Plug size='0.75rem' style={iconStyle} />,
    imports: <LucideImport size='0.75rem' style={iconStyle} />,
    insiderTransactions: <TbTransactionDollar size='0.75rem' style={iconStyle} />,
    ipos: <Gavel size='0.75rem' style={iconStyle} />,
    mAndA: <Group size='0.75rem' style={iconStyle} />,
    news: <TbNews size='0.75rem' style={iconStyle} />,
};

const renderValueSegments = (value: any): FilterButtonGroupSegment[] => {
    if (Array.isArray(value)) {
        return [
            {
                key: 'values',
                label: value.join(', '),
            },
        ];
    }

    if (value?.toggled && value?.categories?.length) {
        return [
            {
                key: 'categories',
                label: value.categories.join(', '),
            },
        ];
    }

    if (value?.startDate || value?.endDate) {
        return [
            {
                key: 'date-range',
                label: (
                    <>
                        Date Range: {value.startDate ? dayjs(value.startDate).format('LL') : 'Any'}{' '}
                        <TbArrowRight style={{ verticalAlign: '-0.125rem', margin: '0 0.25rem' }} />{' '}
                        {value.endDate ? dayjs(value.endDate).format('LL') : 'Any'}
                    </>
                ),
                maxWidth: 16 * 18,
            },
        ];
    }

    if (value?.toggled) {
        return [
            {
                key: 'enabled',
                label: 'Enabled',
            },
        ];
    }

    return [];
};

const PremiumOrEventFilterItem: FC<PremiumOrEventFilterItemProps> = ({
    handleDelete,
    label,
    path,
    value,
}): JSX.Element => {
    const segments: FilterButtonGroupSegment[] = [
        {
            icon: keyIconMap[label],
            key: 'name',
            label: keyNameMap[label] ?? capitalCase(label),
        },
        ...renderValueSegments(value),
    ];

    return <FilterButtonGroup segments={segments} onRemove={() => handleDelete(path)} />;
};

export default PremiumOrEventFilterItem;
